import { runInAction } from 'mobx'
import rootStore from '../..' // Import the root store
import { setRecruitment } from '../../../api/modules/api-recruitment'
import { warnPt } from '../../../config'
import { responseHasError } from '../../users/actions'
import { wrapWithLoading } from '../../../config/general-actions'
import { actSaveEntityForDemo } from '../../monthly-reports/actions'
import { Recruitment } from '../../../cls/modules/recruitment'
import { getFullItm } from '../../../api/utils/api-item'

const recruitmentStore = rootStore.recruitment

/**
 * @async
 * @function newRecruitment
 * @description Creates a new recruitment item with the specified ID and marks it as new.
 * @param {string} newId - The ID of the new recruitment item.
 * @returns {Promise<void>}
 */
export const newRecruitment = async (newId) => {
	warnPt(newId)

	runInAction(() => {
		const _storeItm = requireRecruitmentById(newId)
		_storeItm.setNewFlag()
	})
}

/**
 * @function requireRecruitmentById
 * @description Retrieves a recruitment item by its ID from the store. If it doesn't exist, creates a new recruitment item.
 * @param {string} recruitmentId - The ID of the recruitment item to retrieve.
 * @returns {Recruitment} The retrieved or newly created recruitment item.
 */
export const requireRecruitmentById = (recruitmentId) => {
	warnPt(recruitmentId)
	let recruitment = recruitmentStore.recruitment[recruitmentId]
	if (recruitment) {
		return recruitment
	}
	runInAction(() => {
		recruitmentStore.recruitment[recruitmentId] = new Recruitment(
			recruitmentId
		)
	})
	return recruitmentStore.recruitment[recruitmentId]
}

/**
 * @async
 * @function actLoadRecruitment
 * @description Loads a recruitment item from the server by its ID and updates the store.
 * @param { function } navigate - The navigation function.
 * @param {string} recruitmentId - The ID of the recruitment item to load.
 * @returns {Promise<void>}
 */
export const actLoadRecruitment = wrapWithLoading(
	async (recruitmentId) => {
		const _response = await getFullItm('recruitments', {
			id: recruitmentId
		})

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				const _storeItm = requireRecruitmentById(recruitmentId)
				_storeItm.updateFromServer(_response.data || {})
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Recruitment' }
)

/**
 * @async
 * @function actSaveRecruitment
 * @description Saves a recruitment item to the server.
 * @param {string} recruitmentId - The ID of the recruitment item to save.
 * @param {Object} full - The full data of the recruitment item to save.
 * @returns {Promise<boolean>} True if the save was successful, false otherwise.
 */
export const actSaveRecruitment = wrapWithLoading(
	async (recruitmentId, full, demo = false) => {
		if (demo) {
			actSaveEntityForDemo(
				recruitmentId,
				full,
				requireRecruitmentById,
				rootStore.uiLstRecruitment,
				false
			)

			return true
		}

		const _response = await setRecruitment(recruitmentId, full)

		const hasError = responseHasError(_response)
		if (hasError) {
			throw _response
		}
		return true // No exceptions were thrown, so return true
	},
	{ loadingMessage: 'Saving Recruitment' }
)
